import React, { forwardRef } from "react";
import { Box } from "@mui/material";
import styled from "styled-components";
import ClassicCertificate from "./templates/ClassicCertificate";
import ModernCertificate from "./templates/ModernCertificate";
import ElegantCertificate from "./templates/ElegantCertificate";

const CertificateTemplate = forwardRef(
  (
    {
      selectedStudents,
      schoolDetail,
      selectedSession,
      template = "Classic",
      certificatesPerPage = 1,
      checkBoxEnabled,
      certificateTitle,
      certificateSubtitle,
      courseName,
      description,
      principalName,
      coordinatorName,
      primaryColor,
      secondaryColor,
      accentColor,
      fontFamily,
      borderStyle,
      borderWidth,
      pageOrientation,
      fieldStyles,
    },
    ref
  ) => {
    // Select the appropriate template component
    const renderCertificate = (student, index) => {
      const props = {
        student,
        schoolDetail,
        selectedSession,
        checkBoxEnabled,
        certificateTitle,
        certificateSubtitle,
        courseName,
        description,
        principalName,
        coordinatorName,
        primaryColor,
        secondaryColor,
        accentColor,
        fontFamily,
        borderStyle,
        borderWidth,
        fieldStyles,
        pageOrientation,
      };

      switch (template) {
        case "Modern":
          return <ModernCertificate key={student._id || index} {...props} />;
        case "Elegant":
          return <ElegantCertificate key={student._id || index} {...props} />;
        case "Classic":
        default:
          return <ClassicCertificate key={student._id || index} {...props} />;
      }
    };

    return (
      <CertificateContainer ref={ref} className="certificate-template">
        {selectedStudents.map((student, index) => (
          <React.Fragment key={student._id || index}>
            <PageContainer
              className="certificate-page"
              orientation={pageOrientation}
            >
              {renderCertificate(student, index)}
            </PageContainer>
            {index < selectedStudents.length - 1 && <PageBreak />}
          </React.Fragment>
        ))}
      </CertificateContainer>
    );
  }
);

CertificateTemplate.displayName = "CertificateTemplate";

// Styled Components for Print
const CertificateContainer = styled.div`
  font-family: "Arial", sans-serif;
  background: white;

  @media print {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    margin: 0;
    padding: 0;
    width: 100%;
  }
`;

const PageContainer = styled(Box)`
  width: 100%;
  max-width: ${(props) =>
    props.orientation === "landscape" ? "297mm" : "210mm"};
  min-height: ${(props) =>
    props.orientation === "landscape" ? "210mm" : "297mm"};
  margin: 0 auto;
  background: white;
  box-sizing: border-box;
  position: relative;

  @media screen {
    margin-top: 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    overflow: hidden;

    &:first-child {
      margin-top: 0;
    }
  }

  @media print {
    page-break-inside: avoid;
    page-break-after: always;
    width: 100%;
    max-width: 100%;
    min-height: 100%;
    margin: 0;
    padding: 0;
    box-shadow: none;
    border-radius: 0;

    &:last-child {
      page-break-after: auto;
    }
  }
`;

const PageBreak = styled.div`
  @media all {
    display: none;
  }

  @media print {
    display: block;
    page-break-before: always;
    page-break-inside: avoid;
    margin: 0;
    height: 0;
  }
`;

export default CertificateTemplate;
